"use client";

import React, { useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/lib/i18n/useTranslation';

gsap.registerPlugin(ScrollTrigger);

const GOLD = '#D48937';

const SECTION_LAYOUT = [
    { src: '/images/about/about2.jpeg', align: 'left' },
    { src: '/images/about/about3.jpeg', align: 'right' },
    { src: '/images/about/about4.jpeg', align: 'left' },
    { src: null, align: 'right' },
];

const HERO_IMAGE = '/images/about/about1.jpeg';

const About = () => {
    const containerRef = useRef(null);
    const heroLineRef = useRef(null);
    const chapterRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [chapterVisible, setChapterVisible] = useState(false);

    const { t, locale } = useTranslation();
    const sections = t('about.sections') || [];
    const sectionsCount = sections.length;

    useGSAP(() => {
        const mm = gsap.matchMedia();

        mm.add(
            {
                motionOK: '(prefers-reduced-motion: no-preference)',
                isDesktop: '(min-width: 768px)',
            },
            (context) => {
                const { motionOK, isDesktop } = context.conditions;

                if (!motionOK) {
                    gsap.set('[data-reveal]', { autoAlpha: 1, y: 0 });
                    gsap.set(heroLineRef.current, { scaleX: 1 });
                    return;
                }

                const heroTl = gsap.timeline({ defaults: { ease: 'expo.out' } });
                heroTl
                    .from('[data-hero-eyebrow]', { autoAlpha: 0, y: 16, duration: 1.2 }, 0)
                    .fromTo(
                        '[data-hero-first]',
                        { x: -40, autoAlpha: 0 },
                        { x: 0, autoAlpha: 1, duration: 1.6 },
                        0.05,
                    )
                    .fromTo(
                        '[data-hero-last]',
                        { x: 40, autoAlpha: 0 },
                        { x: 0, autoAlpha: 1, duration: 1.6 },
                        0.05,
                    )
                    .fromTo(
                        heroLineRef.current,
                        { scaleX: 0, transformOrigin: 'center' },
                        { scaleX: 1, duration: 1.4 },
                        0.25,
                    )
                    .from('[data-hero-role]', { autoAlpha: 0, y: 20, duration: 1 }, 0.4)
                    .from('[data-scroll-cue]', { autoAlpha: 0, duration: 0.8 }, 0.8);

                gsap.utils.toArray('[data-reveal]').forEach((el) => {
                    gsap.fromTo(
                        el,
                        { autoAlpha: 0, y: 40 },
                        {
                            autoAlpha: 1,
                            y: 0,
                            duration: 1.1,
                            ease: 'power3.out',
                            scrollTrigger: {
                                trigger: el,
                                start: 'top 85%',
                                once: true,
                            },
                        },
                    );
                });

                gsap.utils.toArray('[data-stagger-words] .word').forEach((el, i, arr) => {
                    gsap.fromTo(
                        el,
                        { autoAlpha: 0, y: 24 },
                        {
                            autoAlpha: 1,
                            y: 0,
                            duration: 0.9,
                            ease: 'power3.out',
                            delay: i * 0.05,
                            scrollTrigger: {
                                trigger: arr[0].parentElement,
                                start: 'top 80%',
                                once: true,
                            },
                        },
                    );
                });

                gsap.utils.toArray('[data-parallax-image]').forEach((el) => {
                    const img = el.querySelector('img');
                    if (!img) return;
                    gsap.to(img, {
                        yPercent: -18,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: el,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: true,
                        },
                    });

                    gsap.fromTo(
                        el,
                        { clipPath: 'inset(8% 8% 8% 8%)' },
                        {
                            clipPath: 'inset(0% 0% 0% 0%)',
                            duration: 1.4,
                            ease: 'power3.out',
                            scrollTrigger: {
                                trigger: el,
                                start: 'top 80%',
                                once: true,
                            },
                        },
                    );
                });

                const heroImg = document.querySelector('[data-hero-image] img');
                if (heroImg) {
                    gsap.to(heroImg, {
                        yPercent: -10,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: '[data-hero-image]',
                            start: 'top top',
                            end: 'bottom top',
                            scrub: true,
                        },
                    });
                }

                if (isDesktop) {
                    const sectionEls = gsap.utils.toArray('[data-section-index]');
                    sectionEls.forEach((el) => {
                        const i = Number(el.dataset.sectionIndex);
                        ScrollTrigger.create({
                            trigger: el,
                            start: 'top 60%',
                            end: 'bottom 40%',
                            onToggle: (self) => {
                                if (self.isActive) setActiveIndex(i);
                            },
                        });
                    });

                    if (chapterRef.current) {
                        ScrollTrigger.create({
                            trigger: chapterRef.current,
                            start: 'top 75%',
                            end: 'bottom 25%',
                            onToggle: (self) => setChapterVisible(self.isActive),
                        });
                    }
                }
            },
        );
    }, { scope: containerRef, dependencies: [locale] });

    return (
        <>
            <div
                ref={containerRef}
                className="relative z-20 min-h-screen bg-gradient-to-br from-white to-gray-200 text-neutral-900"
            >
                <Navbar />

                <main className="font-pp-neue">
                    <section className="relative px-4 pt-32 pb-12 md:px-12 md:pt-40 md:pb-16">
                        <div className="mx-auto w-full max-w-[1400px]">
                            <div
                                data-hero-eyebrow
                                className="mb-10 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em]"
                                style={{ color: GOLD }}
                            >
                                <span>00</span>
                                <span className="h-px w-10 bg-current opacity-60" />
                                <span className="text-neutral-500">{t('about.eyebrow')}</span>
                            </div>

                            <h1 className="flex items-center justify-between whitespace-nowrap text-[12vw] leading-[0.95] md:text-[8vw]">
                                <span data-hero-first className="inline-block">{t('about.name.first')}</span>
                                <span
                                    ref={heroLineRef}
                                    aria-hidden
                                    className="mx-3 hidden h-[3px] w-[18vw] bg-neutral-900 sm:inline-block md:mx-6 md:w-[20vw]"
                                />
                                <span data-hero-last className="inline-block">{t('about.name.last')}</span>
                            </h1>

                            <p
                                data-hero-role
                                className="mt-10 max-w-2xl text-base leading-relaxed text-neutral-600 md:text-xl"
                            >
                                {t('about.role')}
                            </p>
                        </div>

                        <div
                            data-scroll-cue
                            className="pointer-events-none mt-20 flex flex-col items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-neutral-500 md:mt-24"
                        >
                            <span>{t('about.cue')}</span>
                            <span className="h-12 w-px bg-neutral-400" />
                        </div>
                    </section>

                    <section
                        data-hero-image
                        className="relative h-[70vh] w-full overflow-hidden md:h-[88vh]"
                    >
                        <Image
                            src={HERO_IMAGE}
                            alt={t('about.name.first') + ' ' + t('about.name.last')}
                            fill
                            priority
                            sizes="100vw"
                            style={{ objectFit: 'cover' }}
                        />
                    </section>

                    <section className="relative px-4 py-32 md:px-12 md:py-48">
                        <div className="mx-auto max-w-[900px] text-center">
                            <span
                                aria-hidden
                                className="mx-auto mb-12 block h-px w-24"
                                style={{ background: GOLD }}
                            />
                            <blockquote
                                data-stagger-words
                                key={`manifesto-${locale}`}
                                className="text-3xl leading-[1.15] tracking-tight md:text-6xl"
                            >
                                {t('about.manifesto.partA')
                                    .split(' ')
                                    .concat(['/'])
                                    .concat(t('about.manifesto.partB').split(' '))
                                    .map((word, i) => (
                                        <span
                                            key={`${word}-${i}`}
                                            className="word inline-block pr-[0.25em]"
                                            style={word === '/' ? { color: GOLD } : undefined}
                                        >
                                            {word}
                                        </span>
                                    ))}
                            </blockquote>
                            <footer
                                data-reveal
                                className="mt-12 text-[11px] uppercase tracking-[0.3em] text-neutral-500"
                            >
                                — {t('about.manifesto.attribution')}
                            </footer>
                        </div>
                    </section>

                    <div ref={chapterRef} className="relative">
                        <aside
                            aria-hidden
                            className="pointer-events-none fixed right-6 top-1/2 z-30 hidden -translate-y-1/2 flex-col items-end gap-5 transition-opacity duration-500 md:flex"
                            style={{ opacity: chapterVisible ? 1 : 0 }}
                        >
                            {sections.map((_, i) => {
                                const isActive = i === activeIndex;
                                return (
                                    <div
                                        key={`chapter-${i}`}
                                        className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] transition-all duration-500"
                                    >
                                        <span
                                            className="tabular-nums transition-colors duration-500"
                                            style={{
                                                color: isActive ? GOLD : '#9ca3af',
                                                opacity: isActive ? 1 : 0.55,
                                            }}
                                        >
                                            {String(i + 1).padStart(2, '0')}
                                        </span>
                                        <span
                                            className="h-px transition-all duration-500"
                                            style={{
                                                width: isActive ? 32 : 12,
                                                background: isActive ? GOLD : '#9ca3af',
                                                opacity: isActive ? 1 : 0.4,
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </aside>

                        {sections.map((section, index) => {
                            const layout = SECTION_LAYOUT[index] ?? { src: null, align: 'left' };
                            const isLeft = layout.align === 'left';
                            const hasImage = Boolean(layout.src);
                            return (
                                <article
                                    key={`${locale}-section-${index}`}
                                    data-section-index={index}
                                    className="relative px-4 py-24 md:px-12 md:py-40"
                                >
                                    <div className="mx-auto w-full max-w-[1400px]">
                                        <div className="mx-auto max-w-[680px]">
                                            <div data-reveal>
                                                <div
                                                    className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em]"
                                                    style={{ color: GOLD }}
                                                >
                                                    <span className="tabular-nums">
                                                        {String(index + 1).padStart(2, '0')} / {String(sectionsCount).padStart(2, '0')}
                                                    </span>
                                                    <span className="h-px w-10 bg-current opacity-60" />
                                                    <span className="text-neutral-500">{section.title}</span>
                                                </div>
                                            </div>
                                            <h2
                                                data-reveal
                                                className="mt-6 text-3xl leading-tight md:text-5xl"
                                            >
                                                {section.title}
                                            </h2>
                                            <p
                                                data-reveal
                                                className="mt-8 text-lg leading-loose text-neutral-700 md:text-xl"
                                            >
                                                {section.text}
                                            </p>
                                        </div>

                                        {hasImage ? (
                                            <figure
                                                className={`relative mt-16 md:mt-24 ${isLeft ? 'md:-ml-12 md:mr-auto' : 'md:-mr-12 md:ml-auto'} w-full md:w-[88%]`}
                                            >
                                                <div
                                                    data-parallax-image
                                                    className="relative h-[55vh] w-full overflow-hidden md:h-[80vh]"
                                                >
                                                    <Image
                                                        src={layout.src}
                                                        alt={section.alt || section.title}
                                                        fill
                                                        sizes="(max-width: 768px) 100vw, 88vw"
                                                        style={{ objectFit: 'cover' }}
                                                    />
                                                </div>
                                                {section.caption && (
                                                    <figcaption
                                                        data-reveal
                                                        className={`mt-4 text-[11px] uppercase tracking-[0.3em] text-neutral-500 ${isLeft ? 'md:pl-12' : 'md:pr-12 md:text-right'}`}
                                                    >
                                                        {section.caption}
                                                    </figcaption>
                                                )}
                                            </figure>
                                        ) : null}
                                    </div>
                                </article>
                            );
                        })}
                    </div>

                    <section className="relative px-4 pb-40 pt-16 md:px-12 md:pb-56 md:pt-24">
                        <div className="mx-auto max-w-[900px] text-center">
                            <span
                                aria-hidden
                                className="mx-auto mb-10 block h-px w-16"
                                style={{ background: GOLD }}
                            />
                            <Link
                                href="/?category=shop"
                                className="group inline-flex items-baseline gap-3 text-2xl tracking-wide md:text-4xl"
                            >
                                <span className="relative">
                                    {t('about.outro')}
                                    <span
                                        className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-neutral-900 transition-transform duration-500 ease-out group-hover:scale-x-100"
                                    />
                                </span>
                                <span
                                    aria-hidden
                                    className="transition-transform duration-500 ease-out group-hover:translate-x-2"
                                    style={{ color: GOLD }}
                                >
                                    →
                                </span>
                            </Link>
                        </div>
                    </section>
                </main>
            </div>

            <Footer />
        </>
    );
};

export default About;
